import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from '../../../../../components/Spinner';
import AutoSizer from 'react-virtualized-auto-sizer';
import VirtualizedList from '../../../../../components/VirtualizedList';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import DatePicker from 'react-datepicker';
import { NumericFormat } from 'react-number-format';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { toast } from 'react-toastify';
import { makeRequest } from '../../../../../utils/makeRequest';

function NewFundingCustomRepaymentsTab({
  fundingFutureRepayments,
  fundingRepaymentMode,
  fundingId,
  fundingCurrentAmount,
  consumerId,
  refreshTab,
  setRefreshTab,
  setActiveTab,
  setTotalExpectedBalance,
  spinner,
  setSpinner,
  refreshFundingData,
}) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginFunding, apiOriginMonitor } = useEnv();

  const [loading, setLoading] = useState(false);

  const [nextPaydays, setNextPaydays] = useState([]);

  const [tableMode, setTableMode] = useState('read');
  const [editingRepayment, setEditingRepayment] = useState(null);
  const [editingRepaymentIndex, setEditingRepaymentIndex] = useState(null);
  const [newRepayment, setNewRepayment] = useState({ repaymentDate: '', amount: undefined });
  const [futureRepayments, setFutureRepayments] = useState(fundingFutureRepayments);

  const futureRepaymentsSum = useMemo(
    () => futureRepayments.reduce((acc, val) => Number(val.amount) + acc, 0),
    [futureRepayments],
  );

  useEffect(() => {
    setFutureRepayments(fundingFutureRepayments);
  }, [fundingFutureRepayments]);

  useEffect(
    () => {
      if (fundingRepaymentMode === 'CUSTOM') {
        setActiveTab('custom_repayments');
      }

      return () => {
        setActiveTab(prevActiveTab =>
          prevActiveTab === 'custom_repayments' ? 'details' : prevActiveTab,
        );
        setTotalExpectedBalance(null);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fundingRepaymentMode],
  );

  const getNextPaydays = useCallback(async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = { token, url: `${apiOriginMonitor}/paydays`, params: { consumerId } };

    try {
      const response = await makeRequest(config);

      setNextPaydays(response);
    } catch (error) {
      toast.error(error.message);
    }
  }, [apiOriginMonitor, getAccessTokenSilently, consumerId]);

  const refresh = useCallback(async () => {
    setSpinner(true);
    await Promise.allSettled([refreshFundingData(), getNextPaydays()]);
    setSpinner(false);
  }, [getNextPaydays, refreshFundingData, setSpinner]);

  useEffect(() => {
    if (refreshTab === 'custom_repayments') {
      refresh();
      setRefreshTab('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTab]);

  const setNextPayday = useCallback(
    index => {
      const futureRepaymentsIndex = index !== null ? index : futureRepayments.length - 1;

      const nextPayday = nextPaydays
        ?.sort((a, b) => new Date(a.mostProbable).getTime() - new Date(b.mostProbable).getTime())
        ?.find(
          payday =>
            new Date(payday.mostProbable) >
            new Date(futureRepayments[futureRepaymentsIndex]?.repaymentDate),
        );

      if (tableMode === 'add') {
        if (futureRepayments.length === 0) {
          setNewRepayment({
            ...newRepayment,
            repaymentDate: new Date(nextPaydays[0].mostProbable),
          });
        } else if (futureRepayments.length > 0 && nextPayday) {
          setNewRepayment({
            ...newRepayment,
            repaymentDate: new Date(nextPayday.mostProbable),
          });
        }
      } else if (tableMode === 'edit') {
        if (nextPayday) {
          setEditingRepayment({
            ...editingRepayment,
            repaymentDate: new Date(nextPayday.mostProbable),
          });
        }
      }
    },
    [futureRepayments, nextPaydays, tableMode, newRepayment, editingRepayment],
  );

  const resetMode = () => {
    setTableMode('read');
    setNewRepayment({ repaymentDate: '', amount: undefined });
    setEditingRepayment(null);
  };

  const validateTempPlans = useCallback(
    async (action, itemIndex) => {
      setLoading(true);

      let payments = null;

      const formatPayment = payment => ({
        amount: Number(payment.amount),
        repaymentDate: payment.repaymentDate
          ? moment(payment.repaymentDate).format('YYYY-MM-DD')
          : null,
      });

      if (action === 'add') {
        payments = [...futureRepayments, formatPayment(newRepayment)];
      } else if (action === 'edit') {
        payments = futureRepayments.map((fR, index) =>
          itemIndex === index ? formatPayment(editingRepayment) : fR,
        );
      } else if (action === 'delete') {
        payments = futureRepayments.filter((_, index) => index !== itemIndex);
      }

      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginFunding}/validate_temp_plan`,
        method: 'POST',
        data: JSON.stringify({ consumerId, fundingId, consent: true, payments }),
      };

      try {
        const response = await makeRequest(config);

        setTotalExpectedBalance(response.bridgePoints);

        if (response.errors.length > 0) {
          response.errors.forEach(val => toast.error(val));
        } else {
          setFutureRepayments(response.payments);
          resetMode();
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [
      apiOriginFunding,
      getAccessTokenSilently,
      newRepayment,
      editingRepayment,
      consumerId,
      fundingId,
      futureRepayments,
      setTotalExpectedBalance,
    ],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: 'repaymentDate',
        header: 'Repayment Date',
        width: 230,
        renderValue: (value, _, idx, list) => (
          <>
            {tableMode === 'read' && value}
            {tableMode === 'add' &&
              (idx === list.length - 1 ? (
                <div className="d-flex align-items-center">
                  <DatePicker
                    popperPlacement="bottom-end"
                    customInput={<Form.Control />}
                    className="bo-common-popover-form-menu-input"
                    onChange={e =>
                      setNewRepayment(repayment => ({ ...repayment, repaymentDate: e }))
                    }
                    selected={newRepayment.repaymentDate}
                    dateFormat="yyyy-MM-dd"
                    portalId="datepicker-portal"
                  />
                  <Button
                    className="ms-2 bo-common-inline-button"
                    onClick={() => setNextPayday(null)}
                  >
                    NPD
                  </Button>
                </div>
              ) : (
                value
              ))}
            {tableMode === 'edit' &&
              (idx === editingRepaymentIndex ? (
                <div className="d-flex align-items-center">
                  <DatePicker
                    popperPlacement="bottom-end"
                    customInput={<Form.Control />}
                    className="bo-common-popover-form-menu-input"
                    onChange={e =>
                      setEditingRepayment(repayment => ({ ...repayment, repaymentDate: e }))
                    }
                    selected={editingRepayment.repaymentDate}
                    dateFormat="yyyy-MM-dd"
                    portalId="datepicker-portal"
                  />
                  <Button
                    className="ms-2 bo-common-inline-button"
                    onClick={() => setNextPayday(idx)}
                  >
                    NPD
                  </Button>
                </div>
              ) : (
                value
              ))}
          </>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        width: 165,
        renderValue: (value, _, idx, list) => (
          <>
            {tableMode === 'read' && `$${value}`}
            {tableMode === 'add' &&
              (idx === list.length - 1 ? (
                <NumericFormat
                  className="bo-common-popover-form-menu-input"
                  value={newRepayment.amount}
                  customInput={Form.Control}
                  onValueChange={({ floatValue }) =>
                    setNewRepayment(prev => ({ ...prev, amount: floatValue }))
                  }
                  allowNegative={false}
                  decimalScale={0}
                  prefix="$"
                />
              ) : (
                `$${value}`
              ))}
            {tableMode === 'edit' &&
              (idx === editingRepaymentIndex ? (
                <NumericFormat
                  className="bo-common-popover-form-menu-input"
                  value={editingRepayment.amount}
                  customInput={Form.Control}
                  onValueChange={({ floatValue }) =>
                    setEditingRepayment(prev => ({ ...prev, amount: floatValue }))
                  }
                  allowNegative={false}
                  decimalScale={0}
                  prefix="$"
                />
              ) : (
                `$${value}`
              ))}
          </>
        ),
      },
      {
        key: 'action',
        header: '',
        width: 250,
        renderValue: (_, row, idx, list) => (
          <>
            {tableMode === 'read' && (
              <div className="d-flex align-items-center gap-3">
                <Button
                  className="bo-common-inline-button"
                  onClick={() => {
                    setTableMode('edit');
                    setEditingRepayment(row);
                    setEditingRepaymentIndex(idx);
                  }}
                  key="edit"
                >
                  Edit
                </Button>
                <Button
                  className="bo-common-inline-button"
                  onClick={() => validateTempPlans('delete', idx)}
                  key="delete"
                >
                  Delete
                </Button>
              </div>
            )}
            {tableMode === 'add' &&
              (idx === list.length - 1 ? (
                <div className="d-flex align-items-center gap-3">
                  <Button
                    className="bo-common-inline-button"
                    onClick={() => validateTempPlans('add')}
                    key="set"
                    disabled={!newRepayment.repaymentDate || !newRepayment.amount}
                  >
                    Set
                  </Button>
                  <Button
                    className="bo-common-inline-button"
                    onClick={() => resetMode()}
                    key="cancel"
                  >
                    Cancel
                  </Button>
                </div>
              ) : null)}
            {tableMode === 'edit' &&
              (idx === editingRepaymentIndex ? (
                <div className="d-flex align-items-center gap-3">
                  <Button
                    className="bo-common-inline-button"
                    onClick={() => validateTempPlans('edit', idx)}
                    key="set"
                    disabled={
                      !editingRepayment ||
                      !editingRepayment.repaymentDate ||
                      !editingRepayment.amount
                    }
                  >
                    Set
                  </Button>
                  <Button
                    className="bo-common-inline-button"
                    onClick={() => resetMode()}
                    key="cancel"
                  >
                    Cancel
                  </Button>
                </div>
              ) : null)}
          </>
        ),
      },
    ],
    [
      tableMode,
      newRepayment,
      validateTempPlans,
      setNextPayday,
      editingRepayment,
      editingRepaymentIndex,
    ],
  );

  const updateFutureRepayments = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/update_funding_future_payments`,
      method: 'POST',
      data: JSON.stringify({ consumerId, fundingId, consent: true, payments: futureRepayments }),
    };

    try {
      await makeRequest(config);
      await refreshFundingData();
      setTotalExpectedBalance(null);

      toast.success('New future repayments updated successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <Spinner visible={spinner || loading} wrapperStyle={{ height: '100%' }}>
      <div className="d-flex overflow-hidden bo-h-50">
        <div className="d-flex align-items-center bo-min-w-240 p-3 gap-4">
          {fundingCurrentAmount !== futureRepaymentsSum && tableMode === 'read' && (
            <Button className="bo-common-inline-button" onClick={() => setTableMode('add')}>
              Add New Repayment
            </Button>
          )}
          {fundingFutureRepayments !== futureRepayments && tableMode === 'read' && (
            <Button className="bo-common-inline-button" onClick={() => updateFutureRepayments()}>
              Save
            </Button>
          )}
        </div>
        <div className="d-flex align-items-center p-3 border-start bo-min-w-0">
          {fundingCurrentAmount > futureRepaymentsSum && (
            <p className="text-danger bo-common-text-md text-truncate">
              Total repayments does not match current amount debt.{' '}
              <strong>${fundingCurrentAmount - futureRepaymentsSum}</strong> are missing
            </p>
          )}
          {fundingCurrentAmount < futureRepaymentsSum && (
            <p className="text-danger bo-common-text-md">
              Total repayments does not match current amount debt.{' '}
              <strong>${futureRepaymentsSum - fundingCurrentAmount}</strong> are over paid
            </p>
          )}
        </div>
      </div>
      <div className="h-100">
        <AutoSizer>
          {({ height, width }) => (
            <VirtualizedList
              tableRows={
                tableMode === 'add' ? [...futureRepayments, newRepayment] : futureRepayments
              }
              tableColumns={tableColumns}
              headerTopBorder
              width={width}
              height={height - 50}
              rowKey="repaymentDate"
            />
          )}
        </AutoSizer>
      </div>
    </Spinner>
  );
}

export default NewFundingCustomRepaymentsTab;
